<template>
  <div class="running-mode-main">
    <div class="plots">
      <div id="map-plot">
        <div id="map-title-button">
          <svg
            id="map-title-svg"
            viewBox="0 0 11.5 1"
            preserveAspectRatio="xMinYMin"
          >
            <text font-size="0.60pt" x="0.25" y="0.75" fill="#156560">
              Mean Arterial Pressure (mmHg)
            </text>
          </svg>
          <button
            class="image-button map-config-button"
            @click="clickMAPConfigButton"
          ></button>
        </div>
        <div id="zoom-slider-cont">
          <svg
            id="zoom-minus-label"
            preserveAspectRatio="xMaxYMid meet"
            viewBox="0 0 1 1"
          >
            <text font-size="2" x="0.8" y="1" text-anchor="end">-</text>
          </svg>
          <SimpleSlider
            id="zoom-slider"
            name="Lower"
            min="1"
            max="100"
            v-model="zoomRatio"
            v-on:input="zoomChanged"
          ></SimpleSlider>
          <svg
            id="zoom-plus-label"
            preserveAspectRatio="xMinYMid meet"
            viewBox="0 0 1 1"
          >
            <text font-size="2" x="-0.2" y="1" text-anchor="start">+</text>
          </svg>
        </div>
        <MAPPlot
          id="map-plot-canvas"
          :chartKey="key"
          :signal="signal"
          :markers="markers"
          :yMax="yMax"
          :yMin="yMin"
          :xMin="xMin"
          :xMax="xMax"
          :targetMin="targetMin"
          :targetMax="targetMax"
          :startTime="startTime"
          :endTime="endTime"
          :mildHypoColor="selectedHypoColors[1]"
          :severeHypoColor="selectedHypoColors[2]"
        ></MAPPlot>
      </div>
      <TrendPlots
        id="trend-plot"
        :coSignal="co"
        :hrSignal="hr"
        :svrSignal="svr"
        :markers="markers"
        :yMin="trendsYMin"
        :yMax="trendsYMax"
        :xMin="trendsXMin"
        :xMax="trendsXMax"
        :startTime="startTime"
        :endTime="endTime"
      ></TrendPlots>
      <div id="overlay-container">
        <svg style="height: 100.2%; width: 100%">
          <svg
            id="overlay-background"
            x="6%"
            y="0%"
            viewBox="0 0 142 100"
            preserveAspectRatio="none"
          >
            <path
              :d="overlayStyle"
              style="stroke: #818181; stroke-width: 0.4%"
            />
          </svg>
          <line
            x1="81%"
            y1="15%"
            x2="81%"
            y2="100%"
            style="stroke: #00413b; stroke-width: 0.5%"
          />
          <svg
            id="now-background"
            x="77%"
            y="8.5%"
            width="8%"
            height="14%"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMin meet"
          >
            <polygon points="0 0, 100 0, 100 46, 50 52, 0 46" fill="#00413B" />
            <text
              font-size="20"
              x="50"
              y="18"
              fill="white"
              font-weight="bold"
              text-anchor="middle"
            >
              Current
            </text>
            <text
              font-size="20"
              font-weight="bold"
              x="50"
              y="40"
              fill="white"
              text-anchor="middle"
            >
              {{ endTimeString }}
            </text>
          </svg>
          <circle
            cx="81%"
            :cy="nowCircleY"
            r="7"
            fill="#818181"
            v-show="meanBP !== '-'"
          />
          <svg
            x="77%"
            :y="mapLabelY"
            width="8%"
            height="14%"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
            v-show="meanBP !== '-'"
          >
            <polygon points="25 0, 75 0, 75 46, 50 65, 25 46" fill="#00413B" />
            <text
              font-size="16"
              x="50"
              y="16"
              fill="white"
              text-anchor="middle"
            >
              MAP
            </text>
            <text
              font-size="31"
              x="50"
              y="45"
              fill="white"
              text-anchor="middle"
            >
              {{ meanBP }}
            </text>
          </svg>
        </svg>
      </div>
    </div>
    <div class="cumulative-numerics">
      <div class="cumulative-bp">
        <Cumulatives
          :levels="cumulatives"
          :mildHypoColor="selectedHypoColors[1]"
          :severeHypoColor="selectedHypoColors[2]"
          @toggle-hypo-config="toggleHypoConfig"
        ></Cumulatives>
      </div>
      <div class="numerics">
        <svg
          class="bp-numeric"
          viewBox="0 0 16 10"
          preserveAspectRatio="xMinYMin meet"
        >
          <text x="0.5" y="3.5" fill="white" font-size="4">BP</text>
          <g font-weight="bold">
            <text x="9.2" y="6" fill="white" font-size="2.5" text-anchor="end">
              ({{ meanBP }})
            </text>
            <text x="10" y="4" fill="white" font-size="3">
              {{ systolicBP }}
            </text>
            <text x="10" y="8" fill="white" font-size="3">
              {{ diastolicBP }}
            </text>
          </g>
        </svg>
        <svg
          class="hr-numeric"
          viewBox="0 0 16 10"
          preserveAspectRatio="xMinYMax meet"
        >
          <text x="0.5" y="3.5" fill="white" font-size="4">HR</text>
          <g font-weight="bold">
            <text x="10" y="6" fill="white" font-size="3">{{ heartRate }}</text>
          </g>
        </svg>
      </div>
    </div>
    <div class="markers-connections">
      <BolusButton
        class="bolus"
        @add-bolus-marker="clickBolusButton"
      ></BolusButton>
      <InfusionButtons
        class="infusion-rate"
        @add-infusion-up-marker="clickIVUpButton"
        @add-infusion-down-marker="clickIVDownButton"
        @add-infusion-off-marker="clickIVOffButton"
      ></InfusionButtons>
      <VolumeButton
        class="volume"
        @add-volume-marker="clickVolumeButton"
      ></VolumeButton>
      <Connections
        class="connections"
        :signalQualityGood="signalQualityGood"
      ></Connections>
    </div>
    <div class="branding"></div>
    <TherapyDialog
      v-if="showPressorDialog"
      :type="pressorDialogType"
      class="dialog"
      @close-dialog="closeDialogs"
      @add-marker="addMarker"
    ></TherapyDialog>
    <VolumeDialog
      v-show="showVolumeDialog"
      class="volume-dialog"
      @close-dialog="closeDialogs"
      @add-marker="addMarker"
    ></VolumeDialog>
    <MAPConfigDialog
      v-show="showMAPConfigDialog"
      class="map-config-dialog"
      @close-dialog="closeDialogs"
      :yMin="yMin"
      :yMax="yMax"
      @limit-changed="mapYLimitChanged"
    ></MAPConfigDialog>
    <HypoConfigDialog
      v-show="showHypoConfigDialog"
      @close-dialog="closeDialogs"
      @change-hypo-colors="changeHypoColors"
      class="hypo-config-dialog"
    ></HypoConfigDialog>
  </div>
</template>

<script>
import BolusButton from '../components/BolusButton'
import VolumeButton from '../components/VolumeButton'
import InfusionButtons from '../components/InfusionButtons'
import Connections from '../components/ConnectionStatus'
import MAPPlot from '../components/MAPPlot'
import TrendPlots from '../components/TrendPlots'
import Cumulatives from '../components/Cumulatives'
import TherapyDialog from '../components/TherapyDialog'
import VolumeDialog from '../components/VolumeDialog'
import SimpleSlider from '../components/SimpleSlider'
import MAPConfigDialog from '../components/MAPConfigDialog'
import HypoConfigDialog from '../components/HypoConfigDialog'

export default {
  name: 'RunningModeMain',
  props: {
    endTime: Date,
    co: Array,
    hr: Array,
    svr: Array,
    systolicBP: NaN,
    diastolicBP: NaN,
    meanBP: NaN,
    heartRate: NaN,
    cumulatives: Array,
    mapTrend: Array,
    trendsXMin: Number,
    trendsXMax: Number,
    markers: Array
  },
  data() {
    return {
      startTime: Date,
      xMax: Number,
      xMin: Number,
      signal: [],
      zoomRatio: 100,
      key: 0,
      showPressorDialog: false,
      pressorDialogType: '',
      showVolumeDialog: false,
      showConfirmExit: false,
      trendColor: '#1B77D2',
      trendsYMin: -50,
      trendsYMax: 50,
      yMin: 30,
      yMax: 120,
      signalQualityGood: true,
      showMAPConfigDialog: false,
      showHypoConfigDialog: false,
      selectedHypoColors: ['#C5EECD', '#C4C4C4', '#707070'],
      hypoColorOptions: [
        { id: 1, checked: true, colors: ['#C5EECD', '#C4C4C4', '#707070'] },
        { id: 2, checked: false, colors: ['#C5EECD', '#707070', '#000000'] }
      ]
    }
  },
  computed: {
    overlayStyle() {
      let durationMS = this.endTime.getTime() - this.startTime.getTime()
      let durationWindow = 5 * 60 * 1000
      let width = (1 - durationWindow / durationMS) * 106.3 + 0.2
      return `M${width} 8.5 L106.5 8.5 L106.5 99.8 L0.2 99.8 L0.2 77.8 L${width} 77.8 Z`
    },
    mapLabelY() {
      let y =
        100 *
          (0.15 +
            ((this.yMax - this.meanBP) / (this.yMax - this.yMin)) * 0.48) -
        9
      // Limit to top
      y = Math.max(15 - 9, y)
      return y + '%'
    },
    nowCircleY() {
      let y =
        100 *
        (0.15 + ((this.yMax - this.meanBP) / (this.yMax - this.yMin)) * 0.48)
      // Limit to top
      y = Math.max(15, y)
      return y + '%'
    },
    endTimeString() {
      let endTime = this.endTime
      let h = endTime.getHours()
      let m = endTime.getMinutes()
      m = this.checkTime(m)
      return h + ':' + m
    },
    targetMax() {
      return this.$store.state.bpTargetMax
    },
    targetMin() {
      return this.$store.state.bpTargetMin
    },
    mapTrendDuration() {
      return (-0.07 * this.zoomRatio + 8) * 15 * 60
    }
  },
  components: {
    BolusButton,
    VolumeButton,
    InfusionButtons,
    Connections,
    MAPPlot,
    TrendPlots,
    Cumulatives,
    TherapyDialog,
    VolumeDialog,
    SimpleSlider,
    MAPConfigDialog,
    HypoConfigDialog
  },
  watch: {
    mapTrend() {
      // Update visible section of MAP Trend
      this.signal = this.mapTrend.slice(
        Math.max(this.mapTrend.length - this.mapTrendDuration, 0)
      )
      // Update Start Time
      this.startTime = new Date(
        this.endTime.getTime() - 1000 * this.mapTrendDuration
      )
      // Update start x on plot
      this.xMin = this.signal[this.signal.length - 1][0] - this.mapTrendDuration
      this.xMax = this.signal[this.signal.length - 1][0]
    }
  },
  methods: {
    zoomChanged() {
      // Update visible section of MAP Trend
      this.signal = this.mapTrend.slice(
        Math.max(this.mapTrend.length - this.mapTrendDuration, 0)
      )
      // Update Start Time
      this.startTime = new Date(
        this.endTime.getTime() - 1000 * this.mapTrendDuration
      )
      // Update start x on plot
      this.xMin = this.xMax - this.mapTrendDuration
    },
    getTimestamp(date) {
      let day = date.getDay()
      let month = date.toLocaleString('default', { month: 'short' })
      let year = date.getFullYear()
      let h = date.getHours()
      let m = date.getMinutes()
      m = this.checkTime(m)
      return day + ' ' + month + ' ' + year + '  ' + h + ':' + m
    },
    updateCumulatives() {
      let bpTargetMax = this.$store.state.bpTargetMax
      let bpTargetMin = this.$store.state.bpTargetMin
      let mildHypoThreshold = this.$store.state.mildHypoThreshold
      let targetDuration_s = 0
      let mildHypoDuration_s = 0
      let severeHypoDuration_s = 0
      this.mapTrend.forEach(element => {
        if (element[1] < 55) {
          severeHypoDuration_s += 1
        }
        if (element[1] > 55 && element[1] < mildHypoThreshold) {
          mildHypoDuration_s += 1
        }
        if (element[1] > bpTargetMin && element[1] < bpTargetMax) {
          targetDuration_s += 1
        }
      })
      let targetDuration = (targetDuration_s / 60).toFixed(1)
      let mildHypoDuration = (mildHypoDuration_s / 60).toFixed(1)
      let severeHypoDuration = (severeHypoDuration_s / 60).toFixed(1)
      this.cumulatives = [
        [`${bpTargetMin} - ${bpTargetMax} mmHg`, `${targetDuration} mins`],
        [`55 - ${mildHypoThreshold} mmHg`, `${mildHypoDuration} mins`],
        [`< 55 mmHg`, `${severeHypoDuration} mins`]
      ]
    },
    clickBolusButton() {
      this.pressorDialogType = 'bolus'
      this.showVolumeDialog = false
      this.showPressorDialog = true
    },
    clickIVUpButton() {
      this.pressorDialogType = 'iv_up'
      this.showVolumeDialog = false
      this.showPressorDialog = true
    },
    clickIVDownButton() {
      this.pressorDialogType = 'iv_down'
      this.showVolumeDialog = false
      this.showPressorDialog = true
    },
    clickIVOffButton() {
      this.pressorDialogType = 'iv_off'
      this.showVolumeDialog = false
      this.showPressorDialog = true
    },
    clickVolumeButton() {
      this.showVolumeDialog = true
      this.showPressorDialog = false
    },
    closeDialogs() {
      this.showVolumeDialog = false
      this.showPressorDialog = false
      this.showConfirmExit = false
      this.showMAPConfigDialog = false
      this.showHypoConfigDialog = false
    },
    addMarker(type, shortName) {
      this.closeDialogs()
      this.markers.push({
        point: this.signal[this.signal.length - 1],
        type: type,
        shortName: shortName,
        time: this.endTime
      })
    },
    checkTime(i) {
      if (i < 10) {
        i = '0' + i
      } // add zero in front of numbers < 10
      return i
    },
    nanSafe(x) {
      if (isFinite(x)) {
        return x
      } else {
        return '-'
      }
    },
    numberCheck(x) {
      if (typeof x != 'number') {
        return NaN
      } else {
        return x
      }
    },
    mapYLimitChanged(yMin, yMax) {
      this.yMin = Number(yMin)
      this.yMax = Number(yMax)
    },
    clickMAPConfigButton() {
      this.showMAPConfigDialog = !this.showMAPConfigDialog
    },
    toggleHypoConfig() {
      this.showHypoConfigDialog = !this.showHypoConfigDialog
    },
    changeHypoColors(newValue) {
      this.selectedHypoColors = newValue.colors
    }
  }
}
</script>

<style scoped>
text {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.running-mode-main {
  display: grid;
  background-color: #add4c8;
  box-sizing: border-box;
  padding: 1%;
  margin: 0;
  grid-template-columns: 80.7% 19.3%;
  grid-template-rows: 81.8% 18.2%;
}
.plots {
  position: relative;
  grid-row: 1;
  grid-column: 1;
  display: flex;
  flex-direction: column;
  margin: 0 0 0.5% 0;
}
.image-button {
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center;
  border: none;
  background-color: transparent;
}
#overlay-background {
  position: absolute;
  fill: none;
  top: 0;
  left: 0;
  bottom: 0;
  right: 5px;
}
#overlay-container {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 5px;
  pointer-events: none;
}
#map-plot {
  background-color: white;
  margin: 0 0.9% 1.1% 0;
  height: 62.3%;
  min-height: 62.3%;
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 13.5% 86.5%;
}
#map-title-button {
  grid-row: 1;
  grid-column: 1;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: flex-start;
}
#map-title-svg {
  width: 55%;
}
.map-config-button {
  width: 4%;
  background-image: url('../assets/SettingsGear.svg');
}
#map-plot-canvas {
  grid-row: 2;
  grid-column: 1;
  height: 100%;
  min-height: 100%;
  width: 100%;
}
#zoom-slider-cont {
  position: relative;
  grid-row: 2;
  grid-column: 1;
  height: 10%;
  width: 20%;
  align-self: end;
  margin-left: 7%;
  margin-bottom: 1%;
  display: grid;
  grid-template-rows: 100%;
  grid-template-columns: 10% 80% 10%;
  z-index: 2;
}
#zoom-minus-label {
  height: 100%;
  width: 100%;
  grid-row: 1;
  grid-column: 1;
  align-self: center;
  fill: #707070;
}
#zoom-slider {
  grid-row: 1;
  grid-column: 2;
}
#zoom-plus-label {
  height: 100%;
  width: 100%;
  grid-row: 1;
  grid-column: 3;
  align-self: center;
  fill: #707070;
}
#trend-plot {
  background-color: white;
  height: 36%;
  margin: 0 0.9% 0 0;
}
.cumulative-numerics {
  grid-row: 1;
  grid-column: 2;
  display: flex;
  flex-direction: column;
  margin: 0 0 1.6% 0;
}
.cumulative-bp {
  flex-basis: 45%;
  max-height: 45%;
  background-color: white;
  margin: 0 0 0 -3.7%;
}
.numerics {
  flex-basis: 55%;
  max-height: 55%;
  box-sizing: border-box;
  padding: 4% 0 0 2%;
  display: grid;
  grid-template-rows: 48.5% 48.5%;
  grid-row-gap: 3%;
}
.bp-numeric {
  grid-row: 1;
  background-color: #00524c;
  color: white;
  margin: 0;
}
.hr-numeric {
  grid-row: 2;
  background-color: #00524c;
  color: white;
  margin: 0;
}
.markers-connections {
  grid-row: 2;
  grid-column: 1;
  box-sizing: border-box;
  padding: 0.5% 0.8% 0 0;
  display: grid;
  grid-template-columns: 16% 29% 19% 33%;
  column-gap: 1%;
  grid-template-rows: 100%;
}
.bolus {
  background-color: white;
}
.infusion-rate {
  background-color: white;
}
.volume {
  background-color: white;
}
.connections {
  background-color: white;
}
.branding {
  grid-row: 2;
  grid-column: 2;
  background-color: white;
  margin: 2% 0 0 2%;
  background-image: url('../assets/dslogo.svg');
  background-size: 94%;
  background-repeat: no-repeat;
  background-position: center;
}
.dialog {
  display: block;
  grid-row: 1;
  grid-column: 1;
  margin-top: 2%;
  margin-left: 2%;
  width: 50%;
  height: 95%;
  z-index: 3;
}
.volume-dialog {
  display: block;
  grid-row: 1;
  grid-column: 1;
  margin-top: 2%;
  margin-left: 2%;
  width: 50%;
  height: 50%;
  z-index: 3;
}
.map-config-dialog {
  display: block;
  grid-row: 1;
  grid-column: 1;
  margin-top: 15%;
  margin-left: 35%;
  width: 50%;
  height: 60%;
  z-index: 3;
}
.hypo-config-dialog {
  display: block;
  grid-row: 1;
  grid-column: 1;
  margin-top: 6.5%;
  margin-left: 58%;
  width: 39%;
  height: 41%;
  z-index: 3;
}
</style>
